import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './RavenChat.css';
import { MessageRatingControls } from '../MerlinFeedback/MessageRatingControls';
import { FeedbackModal } from '../MerlinFeedback/FeedbackModal';
import {
  MemoryRef,
  MemoryRefState,
  Rating,
  StreamState,
} from '../MerlinFeedback/types';
import { SubmittedDraft } from '../MerlinFeedback/FeedbackForm';
import { submitMerlinFeedback } from '../MerlinFeedback/hooks/useSubmitFeedback';
import { TimeRangePicker } from './autoSuggestions/TimeRangePicker';
import { useAutoSuggestions, SuggestionDropdown } from './autoSuggestions';
import { HistorySidebar } from './HistorySidebar';
import { MemorySessionsPanel } from './MemorySessionsPanel';
import {
  sendChatMessage,
  sendChatMessageStream,
  writeEventToMemory,
  fetchInsightsWithDetails,
  clearInsightsCache,
  extractActorId,
  fetchSessionEvents,
  fetchTicketSessionContent,
  importTicketSession,
  InsightSummary,
  DomainContext,
} from './api';
import { mapEventsToChatMessages } from './historySidebar.utils';
import { QUERY_REGISTRY } from './autoSuggestions/queryRegistry';
import { ResultRenderer, safeParseResponse } from './blocks';
import { SubagentsSidebar, SidebarToolActivity } from './SubagentsSidebar';
import {
  friendlyToolName,
  purposeForTool,
  stepLabelForTool,
  summarizeToolInput,
  summarizeToolOutputDetailed,
} from './tool-display';
import { useApolloClient } from '@apollo/client';
import { ExpandableSection, Spinner } from '@amzn/awsui-components-react';
import ravenLogoSvg from './raven_logo_clean.svg';

import {
  SESSION_NAMES_KEY,
  DARK_MODE_KEY,
  DARK_MODE_CLASS,
  TIME_RANGE_QUERIES,
  THINKING_STAGES,
  NAV_TABS,
} from './constants';
import { useSessionPersistence } from './useSessionPersistence';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SubActivity {
  /** Tool-use id (stable within a specialist invocation). */
  id: string;
  /** Underlying tool name the specialist is calling. */
  tool: string;
  /** Arguments the specialist passed to the tool. Rendered pretty-printed. */
  input?: unknown;
  /** Tool result, shown once it's back. */
  output?: unknown;
  status: 'running' | 'done';
}

/**
 * Items inside a ToolActivity, in the order the stream delivered them.
 * Rendering them in order (rather than grouping reasoning and subcalls
 * separately) lets the user follow the specialist's actual narrative:
 *   "Let me fetch config…" → GetConfig → "Now the metrics…" → GetMetricData
 * rather than all reasoning at the top and all tool calls at the bottom.
 */
type ToolActivityItem =
  | { kind: 'reasoning'; text: string }
  | { kind: 'subcall'; sub: SubActivity };

interface ToolActivity {
  id: string;
  agent: string;
  status: 'running' | 'done';
  /** Ordered timeline of reasoning lines + subcalls observed inside the specialist. */
  items?: ToolActivityItem[];
}

/**
 * Step bullet representing a tool call the supervisor made. The label is
 * either the LLM's pre-tool narration (preferred) or a deterministic
 * fallback from the tool name and input. Clicking the bullet opens the
 * right sidebar on the matching
 * tool-call detail.
 */
interface TimelineStep {
  id: string;
  label: string;
  status: 'running' | 'success' | 'failed';
  /** Links back to ``msg.tools[i].id`` for sidebar lookup. */
  toolCallId: string;
  /** True for inner tool calls nested under a parent node. */
  nested?: boolean;
}

/**
 * Interleaved assistant-message timeline: prose paragraphs the model
 * streamed between tool calls + step bullets for each tool call, in
 * stream order. Renders flat in the chat bubble.
 */
type ChatItem =
  | { kind: 'prose'; id: string; text: string }
  | {
      kind: 'reasoning';
      id: string;
      parentAgent: string;
      text: string;
      toolCallId?: string;
    }
  | { kind: 'step'; step: TimelineStep }
  | { kind: 'state'; id: string; agent: string; state: string; task: string };

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  chartData?: ChartData;
  /** True while tokens are still streaming into `content`. */
  streaming?: boolean;
  /**
   * AgentCore coordinate of the memory event holding this turn. Assistant messages only, and only once
   * the write resolves — its absence is what holds the rating controls inoperable.
   */
  memoryRef?: MemoryRef;
  /**
   * Set on the two error paths of `handleSend`. Held as a field rather than inferred by string-matching
   * `Error: ${msg}` out of `content`, so a response that legitimately begins with "Error:" is not
   * misclassified as a failure.
   */
  streamFailed?: boolean;
  /** Tool calls emitted by the agent during this turn. Retained as the
   * detailed data source for the right-side Subagents panel. */
  tools?: ToolActivity[];
  /** Interleaved prose + step-bullet timeline. Rendered in the chat
   * bubble; ``content`` is kept for the non-streaming final narration
   * fallback and for copy-to-clipboard. */
  items?: ChatItem[];
}

interface ChartData {
  timestamps: number[];
  series: { values: number[]; label: string }[];
  title: string;
}

interface RavenChatProps {
  domainIdentifier: string;
  cellId?: string;
  onBackToOverview?: () => void;
  onTabChange?: (tabRoute: string) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Generate a unique message ID. */
function createMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Create a ChatMessage object. */
function createMessage(
  role: 'user' | 'assistant',
  content: string,
  chartData?: ChartData
): ChatMessage {
  return {
    id: createMessageId(),
    role,
    content,
    timestamp: Date.now(),
    chartData,
  };
}

/**
 * Pretty-print a subagent tool input / output for inline display.
 *
 * Handles four shapes the server can return:
 *   1. ``object`` / ``array``        → ``JSON.stringify(v, null, 2)``
 *   2. ``string`` that parses as JSON → reparse + reformat so nested
 *      structures pretty-print (e.g. a tool result whose text block
 *      contains ``{"response":[{...}]}`` is re-indented to multi-line).
 *   3. ``string`` that isn't JSON    → passed through verbatim (preserves
 *      whitespace, newlines, etc.).
 *   4. primitives / null / undefined → coerced with ``String(v)``.
 *
 * No truncation: the rendered ``<pre>`` already has ``max-height: 240px``
 * with overflow-auto, so large JSON is safely scrollable rather than
 * losing data. Never throws.
 */
function formatJson(value: unknown): string {
  if (value === null || value === undefined) return '';
  try {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      // Cheap heuristic: only try to reparse if it could plausibly be
      // JSON. Avoids the try/catch tax on every plain-text output.
      if (
        (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
        (trimmed.startsWith('[') && trimmed.endsWith(']'))
      ) {
        try {
          return JSON.stringify(JSON.parse(trimmed), null, 2);
        } catch {
          /* not valid JSON — fall through to verbatim */
        }
      }
      return value;
    }
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Format a time range into a human-readable label. */
function formatTimeRangeLabel(range: {
  startTime: number;
  endTime: number;
}): string {
  const hours = Math.round(
    (range.endTime - range.startTime) / (60 * 60 * 1000)
  );
  return hours < 24 ? `last ${hours}h` : `last ${Math.round(hours / 24)}d`;
}

/** Format elapsed seconds into a readable string. */
function formatElapsed(seconds: number): string {
  return seconds < 60
    ? `${seconds}s`
    : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

/** Save a session name to localStorage. */
function saveSessionName(sessionId: string, name: string): void {
  try {
    const names = JSON.parse(localStorage.getItem(SESSION_NAMES_KEY) || '{}');
    if (!names[sessionId]) {
      names[sessionId] = name.slice(0, 60);
      localStorage.setItem(SESSION_NAMES_KEY, JSON.stringify(names));
    }
  } catch {
    // Silently ignore localStorage errors
  }
}

// ─── Sub-Components ──────────────────────────────────────────────────────────

const RavenLogo = React.memo(({ size = 40 }: { size?: number }) => (
  <img
    src={ravenLogoSvg}
    alt="Agent Hawk"
    width={size}
    height={size}
    style={{ objectFit: 'contain' }}
    aria-hidden="true"
  />
));
RavenLogo.displayName = 'RavenLogo';

// Cloudscape chart components — resolved once at module scope
let CloudscapeLineChart: any;
let CloudscapeBox: any;
try {
  CloudscapeLineChart =
    require('@cloudscape-design/components/line-chart').default;
  CloudscapeBox = require('@cloudscape-design/components/box').default;
} catch {
  // Cloudscape not available
}

const MiniChart = React.memo(({ chartData }: { chartData: ChartData }) => {
  if (!CloudscapeLineChart || !CloudscapeBox || !chartData.series?.length)
    return null;

  const series = chartData.series.map((s, idx) => ({
    title: s.label || `Series ${idx + 1}`,
    type: 'line' as const,
    data: s.values.map((v, i) => ({
      x: new Date(chartData.timestamps[i] * 1000),
      y: v,
    })),
  }));

  return (
    <div className="rv-chart-container">
      <CloudscapeLineChart
        series={series}
        height={200}
        xScaleType="time"
        xTitle="Time"
        yTitle={chartData.series[0]?.label || 'Value'}
        hideFilter
        hideLegend={series.length <= 1}
        empty={
          <CloudscapeBox textAlign="center" color="inherit">
            No data
          </CloudscapeBox>
        }
      />
    </div>
  );
});
MiniChart.displayName = 'MiniChart';

/** Renders the grouped timeline items for an assistant message bubble.
 *  Groups reasoning under parent steps, handles DAG badges, verifier chip,
 *  and state indicators. Extracted for readability. */
function renderGroupedTimeline(
  msg: ChatMessage,
  onStepClick: (msgId: string, toolCallId: string) => void
): React.ReactNode[] {
  const items = msg.items!;
  const parentIds = new Set<string>();
  const childrenByParent = new Map<string, typeof items>();

  // Collect ALL top-level step toolCallIds as potential parents
  for (const item of items) {
    if (item.kind === 'step' && !item.step.nested && item.step.toolCallId) {
      parentIds.add(item.step.toolCallId);
    }
  }
  // Collect children: reasoning items that reference a known parent
  for (const item of items) {
    const parentId =
      item.kind === 'step'
        ? item.step.nested
          ? item.step.toolCallId
          : null
        : item.kind === 'reasoning'
        ? item.toolCallId && parentIds.has(item.toolCallId)
          ? item.toolCallId
          : null
        : null;
    if (parentId && parentIds.has(parentId)) {
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId)!.push(item);
    }
  }

  const rendered: React.ReactNode[] = [];

  for (const item of items) {
    // Skip nested tool call steps — they show in the Trace sidebar
    if (item.kind === 'step' && item.step.nested) continue;

    // Skip reasoning that belongs to a parent — rendered after its parent
    if (
      item.kind === 'reasoning' &&
      item.toolCallId &&
      parentIds.has(item.toolCallId)
    )
      continue;

    if (item.kind === 'prose') {
      rendered.push(
        <div key={item.id} className="rv-timeline__prose rv-markdown-content">
          <ResultRenderer blocks={safeParseResponse(item.text)} />
        </div>
      );
    } else if (item.kind === 'reasoning') {
      rendered.push(
        <button
          key={item.id}
          type="button"
          className="rv-timeline__reasoning"
          onClick={() =>
            item.toolCallId && onStepClick(msg.id, item.toolCallId)
          }
          title={
            item.toolCallId
              ? `${item.parentAgent} — click for details`
              : item.parentAgent
          }
        >
          <span className="rv-timeline__reasoning-icon" aria-hidden="true">
            🤖
          </span>
          <span className="rv-timeline__reasoning-text">{item.text}</span>
        </button>
      );
    } else if (item.kind === 'state') {
      if (!msg.streaming) continue;
      rendered.push(
        <div
          key={item.id}
          className="rv-state-indicator"
          role="status"
          aria-live="polite"
        >
          <span className="rv-state-indicator__icon" aria-hidden="true">
            ⚡
          </span>
          <span className="rv-state-indicator__agent">{item.agent}</span>
          <span className="rv-state-indicator__separator" aria-hidden="true">
            ·
          </span>
          <span className="rv-state-indicator__task">
            {item.task || item.state}
          </span>
        </div>
      );
    } else {
      const s = item.step;
      const isVerifier = s.toolCallId === 'node_evidence_verifier';

      if (isVerifier) {
        const verifierTool = msg.tools?.find(
          (t) => t.id === 'node_evidence_verifier'
        );
        const totalCalls =
          verifierTool?.items?.filter((i) => i.kind === 'subcall').length ?? 0;
        const doneCalls =
          verifierTool?.items?.filter(
            (i) => i.kind === 'subcall' && i.sub.status === 'done'
          ).length ?? 0;
        const isDone = s.status !== 'running';
        rendered.push(
          <button
            key={s.id}
            type="button"
            className={`rv-verifier-chip ${
              isDone ? 'rv-verifier-chip--done' : 'rv-verifier-chip--running'
            }`}
            onClick={() => onStepClick(msg.id, s.toolCallId)}
            title="Click to view verification details in Trace"
          >
            <span className="rv-verifier-chip__icon" aria-hidden="true">
              {isDone ? '✓' : ''}
            </span>
            {!isDone && (
              <span className="rv-verifier-chip__spinner" aria-hidden="true" />
            )}
            <span className="rv-verifier-chip__text">
              {isDone ? 'Verified' : 'Verifying results'}
            </span>
            {totalCalls > 0 && !isDone && (
              <span className="rv-verifier-chip__progress">
                ✓ {doneCalls} checks
              </span>
            )}
          </button>
        );
      } else if (
        s.toolCallId.startsWith('node_') &&
        s.toolCallId.endsWith('_end')
      ) {
        // DAG end node — hide entirely
      } else if (
        s.toolCallId.startsWith('node_') &&
        s.toolCallId.endsWith('_start')
      ) {
        // DAG start node — render as a compact badge
        const dagName = s.toolCallId
          .replace(/^node_/, '')
          .replace(/_start$/, '')
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        rendered.push(
          <div key={s.id} className="rv-dag-badge">
            <span className="rv-dag-badge__icon" aria-hidden="true">
              🔬
            </span>
            <span className="rv-dag-badge__label">{dagName}</span>
          </div>
        );
      } else {
        rendered.push(
          <button
            key={s.id}
            type="button"
            className={`rv-step rv-step--${s.status}`}
            onClick={() => onStepClick(msg.id, s.toolCallId)}
            title={`View details for: ${s.label}`}
          >
            <span className="rv-step__bullet" aria-hidden="true">
              ●
            </span>
            <span className="rv-step__label">{s.label}</span>
          </button>
        );
      }

      // If this is a parent step (not verifier), inject reasoning children
      if (
        s.toolCallId &&
        parentIds.has(s.toolCallId) &&
        s.toolCallId !== 'node_evidence_verifier'
      ) {
        const children = childrenByParent.get(s.toolCallId) ?? [];
        for (const child of children) {
          if (child.kind === 'reasoning') {
            rendered.push(
              <button
                key={child.id}
                type="button"
                className="rv-timeline__reasoning rv-timeline__reasoning--nested"
                onClick={() =>
                  child.toolCallId && onStepClick(msg.id, child.toolCallId)
                }
                title={
                  child.toolCallId
                    ? `${child.parentAgent} — click for details`
                    : child.parentAgent
                }
              >
                <span
                  className="rv-timeline__reasoning-icon"
                  aria-hidden="true"
                >
                  🤖
                </span>
                <span className="rv-timeline__reasoning-text">
                  {child.text}
                </span>
              </button>
            );
          }
        }
      }
    }
  }
  return rendered;
}

/**
 * Per-message rating state, held in a Map on `RavenChat` rather than on the message.
 *
 * On the message it would invalidate the memoised bubble for every other message on each update.
 *
 * **This Map is the only source of displayed rating state and it does not survive a reload.** No rating is
 * fetched on mount or on session load, so a response rated before a reload renders unrated, and rating it
 * again overwrites the existing record in place. That is Requirement 14 criterion 9 and an accepted cost —
 * do not add a fetch to close it.
 */
interface RatingState {
  /** Both values are reachable from chat directly, and the form's field 1 offers the same two. */
  recorded?: Rating | null;
  inFlight?: boolean;
  /**
   * Only meaningful before `memoryRef` arrives: `resolving` while attempting, `unavailable` once given up
   * or once it is known there is nothing to attempt against.
   *
   * Written on every path that reaches a completed assistant message, so an absent value means no attempt
   * was ever made rather than one still running — which is what lets the controls default to `unavailable`.
   */
  refState?: MemoryRefState;
}

/** Derived from fields that already exist — no new stream-state field on the message. */
function streamStateOf(msg: ChatMessage): StreamState {
  if (msg.streamFailed) return 'error';
  if (msg.streaming) {
    return msg.content || (msg.items && msg.items.length > 0)
      ? 'streaming'
      : 'pending';
  }
  return 'complete';
}

const MessageBubble = React.memo(
  ({
    msg,
    onStepClick,
    rating,
    onThumbsUp,
    onThumbsDown,
    onAddDetails,
  }: {
    msg: ChatMessage;
    /** Fired when the user clicks a step bullet in the timeline. Carries the
     * owning message id (so the sidebar knows which tools[] to consume) and
     * the specific tool-call id to pre-expand. */
    onStepClick: (msgId: string, toolCallId: string) => void;
    rating: RatingState | undefined;
    onThumbsUp: (msg: ChatMessage) => void;
    onThumbsDown: (msg: ChatMessage) => void;
    /** Reopens the form on an existing rating. Carries the recorded rating so it is preserved, not re-chosen. */
    onAddDetails: (msg: ChatMessage, recorded: Rating) => void;
  }) => {
    const isUser = msg.role === 'user';
    return (
      <div className={`rv-message-row ${isUser ? 'rv-user' : 'rv-agent'}`}>
        <div
          className={`rv-avatar ${
            isUser ? 'rv-user-avatar' : 'rv-agent-avatar'
          }`}
        >
          {isUser ? '→' : <RavenLogo size={18} />}
        </div>
        <div
          className={`rv-bubble ${
            isUser ? 'rv-user-bubble' : 'rv-agent-bubble'
          }`}
        >
          {/* Flat timeline: interleaved prose paragraphs + step bullets.
            Each step bullet is clickable; clicking opens the right
            sidebar on the matching tool-call detail. During streaming the
            timeline grows item-by-item as tokens + tool events arrive. */}
          {!isUser && msg.items && msg.items.length > 0 && (
            <div className="rv-timeline">
              {renderGroupedTimeline(msg, onStepClick)}
              {msg.streaming &&
                !msg.items?.some(
                  (it) => it.kind === 'step' && it.step.status === 'running'
                ) && (
                  <div className="rv-step rv-step--running">
                    <span className="rv-step__bullet" aria-hidden="true">
                      ●
                    </span>
                  </div>
                )}
            </div>
          )}
          {isUser ? (
            <div className="rv-markdown-content">
              <p>{msg.content}</p>
            </div>
          ) : (
            <>
              {msg.content && !msg.streaming && (
                <div className="rv-markdown-content">
                  <ResultRenderer blocks={safeParseResponse(msg.content)} />
                </div>
              )}
              {msg.content && msg.streaming && !msg.items?.length && (
                <div className="rv-markdown-content">
                  <ResultRenderer blocks={safeParseResponse(msg.content)} />
                </div>
              )}
            </>
          )}
          {msg.chartData && <MiniChart chartData={msg.chartData} />}
          <div className="rv-bubble-meta">
            <span>
              {new Date(msg.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          {/* Added after the existing content, chart and timestamp. Nothing above this line changes. */}
          {!isUser && (
            <MessageRatingControls
              stream={streamStateOf(msg)}
              memoryRefState={
                /*
                 * Falls back to `unavailable`, not `resolving`. A message with no entry in the ratings map
                 * has no attempt in progress: restored messages built without a session id, and the
                 * later occurrences in a role collision where `memoryRef` is withheld deliberately, both
                 * land here. Defaulting to `resolving` left those showing controls that were disabled
                 * forever. The live path writes `resolving` itself while it is genuinely attempting.
                 */
                msg.memoryRef ? 'resolved' : rating?.refState ?? 'unavailable'
              }
              ratingInFlight={rating?.inFlight ?? false}
              recordedRating={rating?.recorded ?? null}
              onThumbsUp={() => onThumbsUp(msg)}
              onThumbsDown={() => onThumbsDown(msg)}
              onAddDetails={
                rating?.recorded
                  ? () => onAddDetails(msg, rating.recorded!)
                  : undefined
              }
            />
          )}
        </div>
      </div>
    );
  }
);
MessageBubble.displayName = 'MessageBubble';

function ThinkingBubble() {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const timer = setInterval(
      () => setElapsed(Math.floor((Date.now() - start) / 1000)),
      1000
    );
    return () => clearInterval(timer);
  }, []);

  let current = THINKING_STAGES[0];
  for (const s of THINKING_STAGES) {
    if (elapsed >= s.at) current = s;
  }

  return (
    <div className="rv-message-row rv-agent">
      <div className="rv-avatar rv-agent-avatar">
        <RavenLogo size={20} />
      </div>
      <div className="rv-bubble rv-agent-bubble rv-thinking-bubble-container">
        <div className="rv-thinking-stage" key={current.text}>
          <span className="rv-thinking-stage-icon">{current.icon}</span>
          <span className="rv-thinking-stage-text">{current.text}</span>
        </div>
        <div className="rv-thinking-progress">
          <div className="rv-thinking-progress-bar" />
        </div>
        <div className="rv-thinking-footer">
          <div className="rv-thinking-dots">
            <span />
            <span />
            <span />
          </div>
          <span className="rv-thinking-elapsed">{formatElapsed(elapsed)}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function RavenChat({
  domainIdentifier,
  cellId,
  onBackToOverview,
  onTabChange,
}: RavenChatProps) {
  const apolloClient = useApolloClient();

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Subagent-details sidebar state. Tracks which message's tools[] to
  // display AND which specific tool-call id to pre-expand when the user
  // clicks a step bullet.
  // (Named ``subagentsSidebar*`` to avoid clashing with the history rail's
  // ``sidebarOpen`` state above.)
  const [subagentsSidebarOpen, setSubagentsSidebarOpen] = useState(false);
  const [subagentsSidebarMsgId, setSubagentsSidebarMsgId] = useState<
    string | null
  >(null);
  const [subagentsSidebarSelectedId, setSubagentsSidebarSelectedId] = useState<
    string | null
  >(null);

  /** Open the sidebar on a given message's tool-call detail.
   *  ``msgId`` picks which message's ``tools[]`` to render; ``toolCallId``
   *  pre-expands + scrolls the matching row into view. Clicking the same
   *  step twice keeps the sidebar open but re-targets the row. */
  const handleStepClick = useCallback((msgId: string, toolCallId: string) => {
    setSubagentsSidebarMsgId(msgId);
    setSubagentsSidebarSelectedId(toolCallId);
    setSubagentsSidebarOpen(true);
  }, []);
  const [thinking, setThinking] = useState(false);
  const [sessionId, setSessionId] = useSessionPersistence(domainIdentifier);
  const [pendingSuggestion, setPendingSuggestion] = useState<any | null>(null);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [memoryPanelOpen, setMemoryPanelOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<string | null>(sessionId);

  // Insights panel state
  const [insightsList, setInsightsList] = useState<InsightSummary[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const [hasActiveInsights, setHasActiveInsights] = useState(false);

  // Auto-suggestions
  const {
    filteredSuggestions,
    inputValue,
    showDropdown,
    setInputValue,
    selectSuggestion,
    clearSuggestions,
  } = useAutoSuggestions(domainIdentifier);

  // ── Dark mode sync ──
  useEffect(() => {
    const isDark = localStorage.getItem(DARK_MODE_KEY) === 'true';
    setIsDarkMode(isDark);
    document.body.classList.toggle(DARK_MODE_CLASS, isDark);

    // Lock body scroll while chat is mounted
    const prevOverflow = document.body.style.overflow;
    const prevHeight = document.body.style.height;
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    window.scrollTo(0, 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.height = prevHeight;
    };
  }, []);

  // ── Restore previous session messages on mount ──
  useEffect(() => {
    sessionRef.current = sessionId;
  }, [sessionId]);
  const skipRestoreRef = useRef(false);
  useEffect(() => {
    if (!sessionId) {
      return;
    }
    // Don't restore if another mount-time effect already populated messages
    if (skipRestoreRef.current) {
      return;
    }
    const actorId = extractActorId();
    if (!actorId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        let allEvents: any[] = [];
        let token: string | undefined;
        do {
          const result = await fetchSessionEvents(
            sessionId,
            actorId,
            domainIdentifier,
            token
          );
          allEvents = allEvents.concat(result.events);
          token = result.nextToken;
        } while (token);
        if (
          !cancelled &&
          allEvents.length > 0 &&
          sessionRef.current === sessionId
        ) {
          setMessages(
            mapEventsToChatMessages(allEvents, sessionId) as ChatMessage[]
          );
          setTimeout(() => {
            const container = bottomRef.current?.closest('.rv-messages-area');
            if (container) container.scrollTop = container.scrollHeight;
          }, 50);
        }
      } catch (err: any) {
        console.warn('[RavenChat] Failed to restore session:', err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-import ticket session from URL params ──
  // Handles deep links like ?import_session=287773672611_test-summary-7_D482533782
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const importSession = params.get('import_session');
    if (!importSession) return;

    // Prevent the restore effect from overwriting imported messages
    skipRestoreRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        console.log(
          '[RavenChat] Auto-importing ticket session:',
          importSession
        );
        // First, import the session to the current user's memory
        const result = await importTicketSession(
          importSession,
          domainIdentifier
        );
        if (cancelled) return;

        // Then load the content from the ticket session to display
        let allEvents: any[] = [];
        let token: string | undefined;
        do {
          const evtResult = await fetchTicketSessionContent(
            importSession,
            token
          );
          allEvents = allEvents.concat(evtResult.events);
          token = evtResult.nextToken;
        } while (token);

        if (!cancelled && allEvents.length > 0) {
          /**
           * Mapped against `importSession`, **not** `result.newSessionId` — the two are deliberately
           * different here, which is why this is spelled out.
           *
           * `mapEventsToChatMessages` pairs the session id it is given with each event's own `eventId`, and
           * these events were fetched from `importSession`, so their ids belong to that session. The import
           * copies the turns into `newSessionId` under *fresh* event ids and returns only a count, never the
           * new ids — so pairing `newSessionId` with these ids named an event that exists in neither session.
           * The controls still rendered as rateable and the submission still succeeded, writing a record whose
           * target resolves to nothing.
           *
           * Pointing at the source is also the better target of the two: the analysis being rated is the one
           * the interceptor produced, and it is the session the ticket deep link carries — so a rating given
           * in chat and one given on the ticket page now name the same event, instead of fragmenting across
           * each viewer's private copy.
           *
           * `setSessionId(result.newSessionId)` below is still correct: continuing the conversation writes into
           * the user's own session. Only the restored turns refer back to where they came from.
           */
          const msgs = mapEventsToChatMessages(
            allEvents,
            importSession
          ) as ChatMessage[];
          setSessionId(result.newSessionId);
          setMessages(msgs);
          setTimeout(() => {
            const container = bottomRef.current?.closest('.rv-messages-area');
            if (container) container.scrollTop = container.scrollHeight;
          }, 50);
        }
      } catch (err: any) {
        console.warn(
          '[RavenChat] Failed to auto-import ticket session:',
          err.message
        );
      } finally {
        // Always clean the URL params to prevent infinite retry loops on refresh
        const url = new URL(window.location.href);
        url.searchParams.delete('import_session');
        url.searchParams.delete('actor_id');
        window.history.replaceState({}, '', url.toString());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-scroll: only stick to bottom if the user is already near it ──
  // Forcing scrollTop=scrollHeight on every state change makes streaming
  // re-renders (one per token, one per subagent event) fight the user when
  // they try to scroll up to read earlier content. This "sticky-bottom"
  // heuristic lets the view follow the stream when scrolled down and stay
  // put when scrolled up.
  useEffect(() => {
    const container = bottomRef.current?.closest('.rv-messages-area');
    if (!container) return;
    const STICKY_THRESHOLD_PX = 120;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom <= STICKY_THRESHOLD_PX) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, thinking]);

  // ── Fetch insights on mount / domain change ──
  const [insightsRetryCount, setInsightsRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setInsightsList([]);
    setInsightsError(null);
    setHasActiveInsights(false);
    setInsightsLoading(true);

    (async () => {
      try {
        const result = await fetchInsightsWithDetails(
          apolloClient,
          domainIdentifier
        );
        if (!cancelled) {
          setInsightsList(result.insights);
          setHasActiveInsights(result.hasActive);
        }
      } catch (err: any) {
        if (!cancelled) {
          setInsightsError(err.message || 'Failed to fetch insights');
        }
      } finally {
        if (!cancelled) {
          setInsightsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [domainIdentifier, apolloClient, insightsRetryCount]);

  // ── Prefetch domain context for first message injection ──
  const [domainContext, setDomainContext] = useState<DomainContext | null>(
    null
  );
  const contextSentRef = useRef(false);

  useEffect(() => {
    contextSentRef.current = false;
    setDomainContext(null);

    let cancelled = false;

    (async () => {
      const [insights, health, info, overview, tms, diagDumps, keyEvents] =
        await Promise.allSettled([
          apolloClient.query({
            query: QUERY_REGISTRY.GET_INSIGHTS.gqlQuery,
            variables:
              QUERY_REGISTRY.GET_INSIGHTS.buildVariables(domainIdentifier),
            fetchPolicy: 'cache-first',
          }),
          apolloClient.query({
            query: QUERY_REGISTRY.CLUSTER_HEALTH_SUMMARY.gqlQuery,
            variables:
              QUERY_REGISTRY.CLUSTER_HEALTH_SUMMARY.buildVariables(
                domainIdentifier
              ),
            fetchPolicy: 'cache-first',
          }),
          apolloClient.query({
            query: QUERY_REGISTRY.CLUSTER_INFO.gqlQuery,
            variables:
              QUERY_REGISTRY.CLUSTER_INFO.buildVariables(domainIdentifier),
            fetchPolicy: 'cache-first',
          }),
          apolloClient.query({
            query: QUERY_REGISTRY.RESOURCE_INFO.gqlQuery,
            variables:
              QUERY_REGISTRY.RESOURCE_INFO.buildVariables(domainIdentifier),
            fetchPolicy: 'cache-first',
          }),
          apolloClient.query({
            query: QUERY_REGISTRY.TMS_ISSUES.gqlQuery,
            variables:
              QUERY_REGISTRY.TMS_ISSUES.buildVariables(domainIdentifier),
            fetchPolicy: 'cache-first',
          }),
          apolloClient.query({
            query: QUERY_REGISTRY.DIAGNOSTIC_DUMPS_EXIST.gqlQuery,
            variables:
              QUERY_REGISTRY.DIAGNOSTIC_DUMPS_EXIST.buildVariables(
                domainIdentifier
              ),
            fetchPolicy: 'cache-first',
          }),
          apolloClient.query({
            query: QUERY_REGISTRY.KEY_EVENTS.gqlQuery,
            variables:
              QUERY_REGISTRY.KEY_EVENTS.buildVariables(domainIdentifier),
            fetchPolicy: 'cache-first',
          }),
        ]);
      if (cancelled) return;
      setDomainContext({
        insights:
          insights.status === 'fulfilled'
            ? insights.value.data?.getInsights
            : null,
        clusterHealth: health.status === 'fulfilled' ? health.value.data : null,
        clusterInfo: info.status === 'fulfilled' ? info.value.data : null,
        domainOverview:
          overview.status === 'fulfilled' ? overview.value.data : null,
        tmsIssues:
          tms.status === 'fulfilled' ? tms.value.data?.domain?.tmsIssues : null,
        diagnosticDumpsExist:
          diagDumps.status === 'fulfilled'
            ? diagDumps.value.data?.checkDiagnosticDumpsExist
            : null,
        keyEvents:
          keyEvents.status === 'fulfilled'
            ? keyEvents.value.data?.domainKeyEvents?.keyEvents
            : null,
        fetchedAt: Date.now(),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [domainIdentifier, apolloClient]);

  // ── Handlers ──

  const handleCopyDomain = useCallback(() => {
    const [account, domain] = domainIdentifier.split(':');
    const text = `Account: ${account || domainIdentifier} Domain: ${
      domain || ''
    }`;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }, [domainIdentifier]);

  const handleSelectSession = useCallback(
    (sid: string, msgs: ChatMessage[]) => {
      setSessionId(sid);
      setMessages(msgs);
      // Force scroll to bottom after loading session
      setTimeout(() => {
        const container = bottomRef.current?.closest('.rv-messages-area');
        if (container) container.scrollTop = container.scrollHeight;
      }, 50);
    },
    []
  );

  const executeWithTimeRange = useCallback(
    async (item: any, timeRange?: { startTime: number; endTime: number }) => {
      const userText = timeRange
        ? `Show me ${item.label} (${formatTimeRangeLabel(timeRange)})`
        : `Show me ${item.label}`;

      setMessages((prev) => [...prev, createMessage('user', userText)]);
      setPendingSuggestion(null);
      setThinking(true);

      try {
        const result = await selectSuggestion(item, timeRange);
        const content = result.success
          ? result.data
          : `Error: ${result.error || 'Query failed'}`;
        setMessages((prev) => [
          ...prev,
          createMessage('assistant', content, result.chartData),
        ]);
      } catch (err: any) {
        setMessages((prev) => [
          ...prev,
          createMessage(
            'assistant',
            `Error: ${err.message || 'Failed to execute suggestion'}`
          ),
        ]);
      } finally {
        setThinking(false);
      }
    },
    [selectSuggestion]
  );

  const handleSelectSuggestion = useCallback(
    (item: any) => {
      clearSuggestions();
      if (TIME_RANGE_QUERIES.has(item.queryId)) {
        setPendingSuggestion(item);
        return;
      }
      executeWithTimeRange(item);
    },
    [clearSuggestions, executeWithTimeRange]
  );

  const handleSend = useCallback(
    async (text: string) => {
      setMessages((prev) => [...prev, createMessage('user', text)]);
      setThinking(true);

      // The assistant message is seeded lazily on the first real event so the
      // existing ``ThinkingBubble`` keeps the UI occupied during the 3-5s TTFT
      // gap between request start and the first Bedrock token. Seeding it up
      // front would render a second, empty bubble next to the Thinking
      // indicator.
      const assistantId = createMessageId();
      let assistantSeeded = false;

      const ensureAssistant = () => {
        if (assistantSeeded) return;
        assistantSeeded = true;
        setMessages((prev) => [
          ...prev,
          {
            id: assistantId,
            role: 'assistant',
            content: '',
            timestamp: Date.now(),
            streaming: true,
            tools: [],
          },
        ]);
        // Hand off from the generic ThinkingBubble to the live message bubble.
        setThinking(false);
      };

      const patchAssistant = (patch: (msg: ChatMessage) => ChatMessage) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? patch(m) : m))
        );
      };

      let streamedContent = '';
      let finalContent: string | null = null;
      let errorText: string | null = null;
      let sid: string | null = sessionId;
      // Timeline state: tokens between tool boundaries buffer into
      // ``proseBuffer``; the last sentence of that buffer becomes the next
      // step's label (one sentence of narration before each tool call).
      // Earlier prose is emitted as a standalone ``prose`` timeline item so
      // narrative context is preserved between step bullets.
      let proseBuffer = '';
      let itemSeq = 0;
      const nextItemId = () => `item-${++itemSeq}`;

      /** Split buffered prose into (earlier-context, trailing-step-label).
       *  Rules: an empty buffer yields (null, null). A single short sentence
       *  (≤30 words) is used entirely as the step label. Otherwise the last
       *  sentence (matched by terminal ./?/!) is the label and everything
       *  before is kept as a prose paragraph. */
      const splitProseForStep = (
        buffer: string
      ): { context: string | null; label: string | null } => {
        const trimmed = buffer.trim();
        if (!trimmed) return { context: null, label: null };
        const wordCount = trimmed.split(/\s+/).length;
        if (wordCount <= 30) return { context: null, label: trimmed };
        // Find the last sentence boundary.
        const match = trimmed.match(/^(.*[.!?])\s*([^.!?]+[.!?]?)\s*$/s);
        if (match && match[2].trim()) {
          return { context: match[1].trim(), label: match[2].trim() };
        }
        // No clear sentence split — use the whole buffer as context and
        // let the deterministic fallback generate the step label.
        return { context: trimmed, label: null };
      };

      /** Flush whatever is in proseBuffer and append a running step bullet
       *  for the given tool call. Called on ``tool_start`` events.
       *  ``fallbackLabel`` is used when the prose buffer yields no usable
       *  label (e.g. model didn't narrate before the tool). */
      const appendStepStart = (toolCallId: string, fallbackLabel: string) => {
        const { context, label } = splitProseForStep(proseBuffer);
        proseBuffer = '';
        patchAssistant((m) => {
          // Deduplicate: skip if a step with this toolCallId already exists
          if (
            (m.items ?? []).some(
              (it) => it.kind === 'step' && it.step.toolCallId === toolCallId
            )
          )
            return m;
          const items = [...(m.items ?? [])];
          if (context)
            items.push({ kind: 'prose', id: nextItemId(), text: context });
          items.push({
            kind: 'step',
            step: {
              id: nextItemId(),
              label: label || fallbackLabel,
              status: 'running',
              toolCallId,
            },
          });
          return { ...m, items };
        });
      };

      /** Flip the matching step's status on ``tool_end``. */
      const updateStepStatus = (
        toolCallId: string,
        status: 'success' | 'failed'
      ) => {
        patchAssistant((m) => ({
          ...m,
          items: (m.items ?? []).map((it) =>
            it.kind === 'step' && it.step.toolCallId === toolCallId
              ? { kind: 'step', step: { ...it.step, status } }
              : it
          ),
        }));
      };

      /** Determine pass/fail for a tool call from its output shape. A
       *  tool_end with an ``error`` field or an upstream non-success status
       *  means the tool itself returned a failure (distinct from the
       *  network-level success the transport reports). */
      const toolOutcome = (output: unknown): 'success' | 'failed' => {
        if (output && typeof output === 'object') {
          const o = output as any;
          if ('error' in o) return 'failed';
          if (o.status && String(o.status).toLowerCase() === 'error')
            return 'failed';
        }
        return 'success';
      };

      /** Resolve the index of the currently-active ToolActivity for a given
       *  specialist agent name. When the same specialist is invoked twice in
       *  one turn we need to attribute incoming subagent events to the second
       *  invocation, not the first. Preference order:
       *    1. Most recent running tool with that agent name
       *    2. Most recent matching tool (fallback for events arriving after
       *       the specialist finished — e.g. a late-arriving reasoning line)
       *  Returns -1 if no match. */
      const findActiveToolIndex = (
        tools: ToolActivity[],
        agent: string
      ): number => {
        for (let i = tools.length - 1; i >= 0; i--) {
          if (tools[i].agent === agent && tools[i].status === 'running')
            return i;
        }
        for (let i = tools.length - 1; i >= 0; i--) {
          if (tools[i].agent === agent) return i;
        }
        return -1;
      };

      try {
        const insightsReady = !insightsLoading;
        const shouldSendContext =
          !contextSentRef.current && domainContext != null && insightsReady;
        const enrichedContext = shouldSendContext
          ? {
              ...domainContext,
              insights:
                insightsList.length > 0
                  ? {
                      enriched: insightsList,
                      record: domainContext.insights?.record ?? null,
                    }
                  : domainContext.insights,
            }
          : undefined;
        const stream = sendChatMessageStream(
          text,
          sessionId,
          domainIdentifier,
          {
            cellId,
            context: enrichedContext,
            onSessionId: (newSid) => {
              if (newSid && newSid !== sid) {
                sid = newSid;
                setSessionId(newSid);
              }
            },
          }
        );
        if (shouldSendContext) {
          contextSentRef.current = true;
        }

        for await (const event of stream) {
          switch (event.type) {
            case 'token': {
              const delta = typeof event.delta === 'string' ? event.delta : '';
              if (!delta) break;
              streamedContent += delta;
              proseBuffer += delta;
              ensureAssistant();
              patchAssistant((m) => ({ ...m, content: streamedContent }));
              break;
            }
            case 'tool_start': {
              const toolId = String(event.id ?? '');
              const agent = String(event.agent ?? 'tool');
              const toolName = String((event as any).tool ?? agent);
              const category = (event as any).category as string | undefined;

              // Skip routing/orchestration internals — not user-facing
              if (category === 'routing') break;

              // For DAG nodes, use the tool name as the display agent
              const isDAGNode =
                category === 'dag_node' || toolId.startsWith('node_');
              const tool: ToolActivity = {
                id: toolId,
                agent: isDAGNode ? toolName : agent,
                status: 'running',
                _input: (event as any).input,
              } as any;
              ensureAssistant();
              // Deduplicate: skip if a ToolActivity with this id already exists
              patchAssistant((m) => {
                if ((m.tools ?? []).some((t) => t.id === toolId)) return m;
                return { ...m, tools: [...(m.tools ?? []), tool] };
              });
              // Emit a step bullet for the timeline.
              const fallback = stepLabelForTool(toolName, (event as any).input);
              appendStepStart(toolId, fallback);
              break;
            }
            case 'tool_end': {
              const toolId = String(event.id ?? '');
              const output = (event as any).output;
              const toolName = String((event as any).tool ?? '');
              const category = (event as any).category as string | undefined;

              // Skip routing internals (no ToolActivity was created for them)
              if (category === 'routing') break;

              ensureAssistant();
              patchAssistant((m) => ({
                ...m,
                tools: (m.tools ?? []).map((t) => {
                  if (t.id !== toolId) return t;
                  // If this ToolActivity has no items (direct tool call, not a
                  // DAG node with nested subcalls), store the output as a
                  // self-referencing SubActivity so the sidebar has content.
                  const items =
                    t.items && t.items.length > 0
                      ? t.items
                      : [
                          {
                            kind: 'subcall' as const,
                            sub: {
                              id: toolId,
                              tool: toolName || t.agent,
                              input: (t as any)._input,
                              output,
                              status: 'done' as const,
                            },
                          },
                        ];
                  return { ...t, status: 'done' as const, items };
                }),
              }));
              updateStepStatus(toolId, toolOutcome(output));
              break;
            }
            case 'subagent_tool_start': {
              // Node boundaries (id=node_*) are handled by tool_start.
              // Only process inner tool calls (id=tooluse_*) here.
              const parent = String((event as any).parent_agent ?? '');
              const subToolName = String((event as any).tool ?? 'tool');
              const subInput = (event as any).input;
              const subId = String((event as any).id ?? '');
              const nodeId = (event as any).node_id as string | undefined;
              const isNodeBoundary = subId.startsWith('node_');

              // Skip node boundary events — tool_start already created the ToolActivity
              if (isNodeBoundary) break;

              const sub: SubActivity = {
                id: subId,
                tool: subToolName,
                input: subInput,
                status: 'running',
              };
              ensureAssistant();
              patchAssistant((m) => {
                const baseTools = m.tools ?? [];

                // Attach to the owning node's ToolActivity using node_id
                const ownerNodeId = nodeId ? `node_${nodeId}` : null;
                let targetIdx = -1;
                if (ownerNodeId) {
                  targetIdx = baseTools.findIndex((t) => t.id === ownerNodeId);
                }
                // Fallback: find by parent_agent (for non-DAG specialist paths)
                if (targetIdx < 0) {
                  targetIdx = findActiveToolIndex(baseTools, parent);
                }

                const tools =
                  targetIdx >= 0
                    ? baseTools.map((t, i) =>
                        i === targetIdx
                          ? {
                              ...t,
                              items: [
                                ...(t.items ?? []),
                                { kind: 'subcall' as const, sub },
                              ],
                            }
                          : t
                      )
                    : baseTools;

                // Inner tool calls don't get their own timeline step —
                // they appear nested under their parent node in the sidebar.
                return { ...m, tools, items: m.items };
              });
              break;
            }
            case 'subagent_tool_end': {
              const parent = String((event as any).parent_agent ?? '');
              const subId = String((event as any).id ?? '');
              const output = (event as any).output;
              const nodeId = (event as any).node_id as string | undefined;
              const isNodeBoundary = subId.startsWith('node_');

              // Skip node boundary events — tool_end handles closing the ToolActivity
              if (isNodeBoundary) break;

              ensureAssistant();
              patchAssistant((m) => {
                const baseTools = m.tools ?? [];

                // Find target via node_id or parent_agent fallback
                const ownerNodeId = nodeId ? `node_${nodeId}` : null;
                let targetIdx = -1;
                if (ownerNodeId) {
                  targetIdx = baseTools.findIndex((t) => t.id === ownerNodeId);
                }
                if (targetIdx < 0) {
                  targetIdx = findActiveToolIndex(baseTools, parent);
                }

                const tools =
                  targetIdx >= 0
                    ? baseTools.map((t, i) => {
                        if (i !== targetIdx) return t;
                        return {
                          ...t,
                          items: (t.items ?? []).map((item) =>
                            item.kind === 'subcall' && item.sub.id === subId
                              ? {
                                  kind: 'subcall' as const,
                                  sub: {
                                    ...item.sub,
                                    output,
                                    status: 'done' as const,
                                  },
                                }
                              : item
                          ),
                        };
                      })
                    : baseTools;

                return { ...m, tools };
              });
              break;
            }
            case 'subagent_reasoning': {
              // Append a reasoning line to the owning node's or specialist's
              // timeline. Uses node_id for DAG grouping when available.
              const parent = String((event as any).parent_agent ?? '');
              const text = String((event as any).text ?? '').trim();
              const nodeId = (event as any).node_id as string | undefined;
              if (!text) break;
              ensureAssistant();
              patchAssistant((m) => {
                const baseTools = m.tools ?? [];
                // Find the owning ToolActivity: by node_id first, then by parent_agent
                const ownerNodeId = nodeId ? `node_${nodeId}` : null;
                let targetIdx = -1;
                if (ownerNodeId) {
                  targetIdx = baseTools.findIndex((t) => t.id === ownerNodeId);
                }
                if (targetIdx < 0) {
                  targetIdx = findActiveToolIndex(baseTools, parent);
                }

                const tools =
                  targetIdx >= 0
                    ? baseTools.map((t, i) =>
                        i === targetIdx
                          ? {
                              ...t,
                              items: [
                                ...(t.items ?? []),
                                { kind: 'reasoning' as const, text },
                              ],
                            }
                          : t
                      )
                    : baseTools;
                // Also surface in the MAIN timeline
                const parentToolCallId =
                  targetIdx >= 0 ? tools[targetIdx].id : '';
                const timeline = [
                  ...(m.items ?? []),
                  {
                    kind: 'reasoning' as const,
                    id: nextItemId(),
                    parentAgent: parent,
                    text,
                    toolCallId: parentToolCallId || undefined,
                  },
                ];
                return { ...m, tools, items: timeline };
              });
              break;
            }
            case 'subagent_state': {
              // Supervisor emits state transitions as it routes between
              // sub-agents. Render as a status indicator in the timeline so
              // the user sees real-time routing decisions.
              const agent = String((event as any).agent ?? '');
              const state = String((event as any).state ?? '');
              const task = String((event as any).task ?? '');
              if (!agent && !state) break;
              ensureAssistant();
              patchAssistant((m) => {
                const items: ChatItem[] = [...(m.items ?? [])];
                // Replace any previous state item (only latest state matters)
                // to avoid cluttering the timeline with transient transitions.
                const filtered: ChatItem[] = items.filter(
                  (it) => it.kind !== 'state'
                );
                filtered.push({
                  kind: 'state' as const,
                  id: nextItemId(),
                  agent,
                  state,
                  task,
                });
                return { ...m, items: filtered };
              });
              break;
            }
            case 'subagent_token': {
              // Specialist agent streaming tokens — informational only.
              // NOT rendered in the main bubble (supervisor's token events +
              // final provide the authoritative user-facing content).
              break;
            }
            case 'final': {
              const raw = (event as { content?: unknown }).content;
              if (typeof raw === 'string') {
                finalContent = raw;
              } else if (raw != null) {
                try {
                  finalContent = JSON.stringify(raw);
                } catch {
                  finalContent = String(raw);
                }
              }
              // Clear proseBuffer — the final content will be rendered via
              // msg.content after streaming ends. Flushing prose here would
              // duplicate the text (once in timeline, once in content).
              proseBuffer = '';
              break;
            }
            case 'error': {
              const msg =
                (event as { message?: string }).message ||
                'Agent stream failed.';
              const kind =
                (event as { error_type?: string }).error_type || 'Error';
              errorText = `${kind}: ${msg}`;
              break;
            }
            case 'heartbeat':
              // Keep-alive only — nothing to do.
              break;
            default:
              // Forward-compatible: silently drop unknown event types.
              break;
          }
        }

        const assistantText = errorText
          ? errorText
          : finalContent ?? (streamedContent || 'No response from agent.');

        // Update UI with final response
        ensureAssistant();
        patchAssistant((m) => ({
          ...m,
          content: assistantText,
          streaming: false,
          ...(errorText ? { streamFailed: true } : {}),
        }));

        /*
         * Everything below is wrapped in its own try/catch, and that isolation is the point.
         *
         * The answer has already been committed by the `patchAssistant` above. Anything that throws from here
         * on would otherwise fall into the outer catch, which overwrites `content` with `Error: …` and sets
         * `streamFailed` — destroying a good, rendered response because a *rating identifier* could not be
         * obtained. That trades the valuable thing for the incidental one.
         *
         * `writeEventToMemory` is documented never to reject, and returns `null` on failure. But that makes
         * the correctness of the rendered transcript depend on an invariant in another module holding forever,
         * across every future edit to it. The isolation costs nothing and removes the dependency.
         */
        try {
          if (sid && !errorText) {
            // Only persist successful turns. Session name is saved on first turn.
            if (!sessionId) {
              saveSessionName(sid, text);
            }
            /*
             * Stated explicitly, because `resolving` used to exist only as the fallback in the controls'
             * `?? 'resolving'`. That made "no entry in the map" mean "an attempt is in progress", which is
             * true here but false for a restored message that never had one — and those sat visible and
             * disabled for the life of the page. Writing it here lets the fallback say `unavailable` instead,
             * so an absent entry means absent rather than pending.
             */
            setRatingState(assistantId, { refState: 'resolving' });
            /**
             * Awaited plainly — no timer, no `Promise.race`.
             *
             * The rendered response does not wait on this: content, chart and timestamp were patched above and
             * the user is already free to send another message. Only the rating controls wait, which is why
             * there is no deadline to enforce — an unresolved identifier has nothing to fall back to, so a
             * timeout could only render controls that cannot work.
             */
            const ref = await writeEventToMemory(
              sid,
              text,
              assistantText,
              domainIdentifier
            );
            if (ref) {
              patchAssistant((m) => ({ ...m, memoryRef: ref }));
            } else {
              // Retries exhausted. The controls stay visible and inoperable, and say so on activation.
              setRatingState(assistantId, { refState: 'unavailable' });
              emitUnrateableOnce(assistantId);
            }
          } else if (!errorText) {
            /**
             * A successful turn with no session id. Nothing identifies the event, so there is nothing to rate.
             *
             * Needed because `refState` is otherwise never written on this path, and the controls derive
             * `msg.memoryRef ? 'resolved' : refState ?? 'resolving'` — so they would sit visible and disabled
             * for the life of the page, which reads as a stuck spinner rather than an answer that cannot be
             * rated. `unavailable` renders no controls at all, which is the honest state.
             *
             * Deliberately does not call `emitUnrateableOnce`: that metric counts ratings lost to retry
             * exhaustion, and a turn that never had a session is a different cause. Folding it in here would
             * change what the metric means without anywhere saying so.
             */
            setRatingState(assistantId, { refState: 'unavailable' });
          }
        } catch (memoryErr: any) {
          /*
           * Swallowed on purpose. The answer stands; only rating it is lost, which is exactly what
           * `unavailable` says — so this reports the same outcome as an exhausted write rather than
           * escalating a rating problem into a transcript problem.
           */
          console.warn(
            '[Merlin] memory write threw; response stands but cannot be rated:',
            memoryErr?.message ?? memoryErr
          );
          setRatingState(assistantId, { refState: 'unavailable' });
        }
      } catch (err: any) {
        const msg = err?.message || 'Failed to send message';
        ensureAssistant();
        patchAssistant((m) => ({
          ...m,
          content: `Error: ${msg}`,
          streaming: false,
          // Marked as a field rather than inferred later by string-matching `Error:` out of the content — a
          // response that legitimately starts with "Error:" must not be misclassified as a failed stream.
          streamFailed: true,
        }));
      } finally {
        setThinking(false);
      }
    },
    [
      sessionId,
      domainIdentifier,
      cellId,
      insightsLoading,
      insightsList,
      domainContext,
    ]
  );

  // ── Derived state ──
  // Also block Send while any assistant message is still streaming — the
  // `thinking` flag flips false on the first stream token (so the
  // ThinkingBubble can hand off to the live message bubble), but the stream
  // is still running after that. Allowing a second submit would run two
  // concurrent handleSend loops with overlapping setState calls.
  // ── Merlin feedback ──
  /**
   * Rating state per message id. Deliberately not on the message: putting it there would invalidate the
   * memoised bubble for every other message on each rating update.
   */
  const [ratings, setRatings] = useState<Map<string, RatingState>>(new Map());
  const [feedbackFor, setFeedbackFor] = useState<{
    msg: ChatMessage;
    rating: Rating;
  } | null>(null);
  const feedbackOpenIdRef = useRef<string>('');
  /** At most one unrateable counter per response, however many times the write is retried. */
  const unrateableEmittedRef = useRef<Set<string>>(new Set());

  const setRatingState = useCallback((id: string, patch: RatingState) => {
    setRatings((prev) => {
      const next = new Map(prev);
      next.set(id, { ...(next.get(id) ?? {}), ...patch });
      return next;
    });
  }, []);

  const emitUnrateableOnce = useCallback((id: string) => {
    if (unrateableEmittedRef.current.has(id)) return;
    unrateableEmittedRef.current.add(id);
    // Fires on retry exhaustion, not on first failure — otherwise a transient blip counts as lost feedback.
    console.warn('[Merlin] unrateable response', { messageId: id });
  }, []);

  /** Thumbs-down opens the form pre-set to No, so the rating is never lost if the form is dismissed. */
  const handleThumbsDown = useCallback((msg: ChatMessage) => {
    feedbackOpenIdRef.current = `open-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    setFeedbackFor({ msg, rating: 'Down' });
  }, []);

  /**
   * Opens the form on an already-recorded rating, preserving that rating rather than choosing one.
   *
   * A fresh `formOpenId` is minted because this is a new form opening — the id identifies the sitting, not
   * the record, and reusing the one from the original submission would make two sittings indistinguishable.
   *
   * Submitting from here overwrites the existing item rather than adding one: the sort key carries no
   * timestamp, so the same kind, target and submitter address the same record.
   */
  const handleAddDetails = useCallback((msg: ChatMessage, recorded: Rating) => {
    feedbackOpenIdRef.current = `open-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    setFeedbackFor({ msg, rating: recorded });
  }, []);
  /** Thumbs-up records `Yes` directly and opens no form. */
  const handleThumbsUp = useCallback(
    async (msg: ChatMessage) => {
      if (!msg.memoryRef) return;
      setRatingState(msg.id, { inFlight: true });
      try {
        await submitMerlinFeedback(apolloClient, {
          triggerPoint: 'CHAT',
          rating: 'Up',
          formOpenId: `up-${Date.now()}`,
          domainIdentifier,
          sessionId: msg.memoryRef.sessionId,
          eventId: msg.memoryRef.eventId,
          messageRole: 'ASSISTANT',
          wrongPartTags: [],
        });
        setRatingState(msg.id, { recorded: 'Up', inFlight: false });
      } catch {
        // Both controls stay activatable and the prior state is restored — the answer is unaffected.
        setRatingState(msg.id, { inFlight: false });
      }
    },
    [apolloClient, domainIdentifier, setRatingState]
  );

  /**
   * Activation without a resolved identifier writes nothing and asks for the identifier again.
   *
   * There is no second write to attempt here — the turn is over — so this only moves the state out of
   * `resolving` so the inline message can appear. A genuine re-attempt would need the turn's text, which
   * `handleSend` no longer holds.
   */
  const submitFeedback = useCallback(
    async (draft: SubmittedDraft): Promise<string> => {
      const target = feedbackFor;
      if (!target || !target.msg.memoryRef) {
        throw new Error(
          'Cannot submit feedback without a resolved memory reference'
        );
      }
      const result = await submitMerlinFeedback(apolloClient, {
        triggerPoint: 'CHAT',
        rating: draft.rating,
        whatShouldItHaveSaid: draft.whatShouldItHaveSaid,
        whatDidWeGetWrong: draft.whatDidWeGetWrong,
        wrongPartTags: draft.wrongPartTags,
        formOpenId: feedbackOpenIdRef.current,
        domainIdentifier,
        sessionId: target.msg.memoryRef.sessionId,
        eventId: target.msg.memoryRef.eventId,
        messageRole: 'ASSISTANT',
      });
      // Recorded verbatim. This used to fold `Partial` onto `No`, because the controls could only show two
      // states while the form offered three. The vocabularies now match, so folding would only lose detail.
      setRatingState(target.msg.id, { recorded: draft.rating });
      return result.feedbackRecordId;
    },
    [apolloClient, domainIdentifier, feedbackFor, setRatingState]
  );

  const isInFlight = thinking || messages.some((m) => m.streaming);
  const canSend = inputValue.trim().length > 0 && !isInFlight;
  const isEmpty = messages.length === 0 && !thinking;

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSend) return;
      handleSend(inputValue.trim());
      setInputValue('');
    },
    [canSend, handleSend, inputValue, setInputValue]
  );

  // ── Render ──
  return (
    <div className={`rv-app ${isDarkMode ? 'theme-dark' : ''}`}>
      {/* Left Rail */}
      <div
        className={`rv-left-rail ${
          sidebarOpen ? 'rv-left-rail--expanded' : ''
        }`}
      >
        <div className="rv-left-rail__header">
          <div
            className="rv-brand-logo"
            onClick={() => onTabChange?.('overview')}
            style={{ cursor: 'pointer' }}
            title="Back to Overview"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onTabChange?.('overview')}
          >
            <img
              className="rv-brand-logo-img"
              src={ravenLogoSvg}
              alt="Agent Hawk"
            />
          </div>
          {sidebarOpen && (
            <>
              <div className="rv-brand-text">
                <div className="rv-brand-title">Agent Hawk</div>
              </div>
              <button
                className="rv-sidebar__close"
                onClick={() => setSidebarOpen(false)}
                aria-label="Close sidebar"
                style={{ marginLeft: 'auto' }}
              >
                ×
              </button>
            </>
          )}
        </div>
        {!sidebarOpen && (
          <button
            className="rv-icon-btn rv-rail-toggle"
            onClick={() => setSidebarOpen(true)}
            aria-label="Expand sidebar"
          >
            ☰
          </button>
        )}
        {!sidebarOpen && (
          <button
            className="rv-icon-btn rv-rail-toggle"
            onClick={() => {
              setSessionId(null);
              setMessages([]);
            }}
            disabled={isInFlight}
            aria-label="New chat"
            title={isInFlight ? 'Wait for response to complete' : 'New chat'}
          >
            +
          </button>
        )}
        {!sidebarOpen && (
          <button
            className="rv-icon-btn rv-rail-toggle"
            onClick={() => setMemoryPanelOpen(true)}
            aria-label="Ticket analysis sessions"
            title="Ticket Analyses"
          >
            🎫
          </button>
        )}
        {sidebarOpen && (
          <div
            className="rv-sidebar-actions"
            style={{
              display: 'flex',
              gap: '8px',
              padding: '8px 18px',
              flexShrink: 0,
            }}
          >
            <button
              className="rv-memory-btn"
              onClick={() => setMemoryPanelOpen(true)}
              title="View ticket analysis sessions"
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                border: '1px solid var(--glass-border)',
                borderRadius: '12px',
                background: 'rgba(139, 92, 246, 0.08)',
                color: 'var(--text-1)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: '180ms',
                fontFamily: 'inherit',
              }}
            >
              🎫 Ticket Analyses
            </button>
          </div>
        )}
        {sidebarOpen && (
          <HistorySidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            onSelectSession={handleSelectSession}
            currentSessionId={sessionId}
            domainIdentifier={domainIdentifier}
            disabled={isInFlight}
          />
        )}
      </div>

      {/* Main Content */}
      <main className="rv-main">
        {/* Top Bar */}
        <nav className="rv-topbar" aria-label="Domain navigation">
          <div className="rv-tab-bar" role="tablist">
            {NAV_TABS.map((tab) => (
              <button
                key={tab.id}
                className={`rv-tab ${
                  tab.id === 'agenthawk' ? 'rv-tab--active' : ''
                }`}
                onClick={() => tab.id !== 'agenthawk' && onTabChange?.(tab.id)}
                role="tab"
                aria-selected={tab.id === 'agenthawk'}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="rv-domain-ident">
            {domainIdentifier}
            <button
              className="rv-domain-copy"
              onClick={handleCopyDomain}
              aria-label="Copy domain info"
              title="Copy domain info"
            >
              {copied ? '✓' : '📋'}
            </button>
          </div>
        </nav>

        <div className="rv-chat-body">
          {/* Loading indicator */}
          {insightsLoading && isEmpty && (
            <div className="rv-insights-loading-inline">
              <div className="rv-insights-spinner" />
              <span>Fetching active insights…</span>
            </div>
          )}

          {/* Landing view */}
          {isEmpty && (
            <div
              className={`rv-landing ${
                (hasActiveInsights || insightsError) && !insightsLoading
                  ? 'rv-landing--split'
                  : 'rv-landing--full'
              }`}
            >
              <LandingHero
                insightsLoading={insightsLoading}
                hasActiveInsights={hasActiveInsights}
                insightCount={insightsList.length}
                onQuickCommand={() => setInputValue('/cpu')}
              />
              {(hasActiveInsights || insightsError) && !insightsLoading && (
                <InsightsPanel
                  insights={insightsList}
                  error={insightsError}
                  loading={insightsLoading}
                  onRetry={() => {
                    clearInsightsCache(domainIdentifier);
                    setInsightsRetryCount((c) => c + 1);
                  }}
                  domainIdentifier={domainIdentifier}
                />
              )}
            </div>
          )}

          {/* Messages */}
          {!isEmpty && (
            <div className="rv-messages-area" role="log" aria-live="polite">
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  onStepClick={handleStepClick}
                  rating={ratings.get(msg.id)}
                  onThumbsUp={handleThumbsUp}
                  onThumbsDown={handleThumbsDown}
                  onAddDetails={handleAddDetails}
                />
              ))}
              {thinking && <ThinkingBubble />}
              <div ref={bottomRef} />
            </div>
          )}
          {/*
            The only host of the form on the chat Trigger_Point. Every exit other than Submit — the close
            control, Escape, an overlay click and Skip alike — is a pure close that writes nothing.
          */}
          <FeedbackModal
            visible={feedbackFor !== null}
            initialRating={feedbackFor?.rating ?? null}
            formOpenId={feedbackOpenIdRef.current}
            onSubmit={submitFeedback}
            onClose={() => setFeedbackFor(null)}
          />

          {/* Input */}
          <div className="rv-input-area rv-input-area--bottom">
            {pendingSuggestion && (
              <TimeRangePicker
                label={pendingSuggestion.label}
                onSelect={(startTime, endTime) =>
                  executeWithTimeRange(pendingSuggestion, {
                    startTime,
                    endTime,
                  })
                }
                onCancel={() => setPendingSuggestion(null)}
              />
            )}
            {showDropdown && !pendingSuggestion && (
              <SuggestionDropdown
                suggestions={filteredSuggestions}
                onSelect={handleSelectSuggestion}
              />
            )}
            <form className="rv-inputbar" onSubmit={onSubmit}>
              <label htmlFor="rv-chat-input" className="rv-sr-only">
                Message
              </label>
              <input
                id="rv-chat-input"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask anything about your domain..."
                disabled={thinking}
                autoComplete="off"
              />
              <button
                type="submit"
                className="rv-inputbar__send"
                disabled={!canSend}
                aria-label="Send message"
              >
                ↑
              </button>
            </form>
            {/* Footer: "Powered by …" and the Send feedback button share one
                row (footer text left, feedback button right). The button opens
                the team Slack channel in a new tab. */}
            <div className="rv-footer-row">
              <div className="rv-footer-note">
                Powered by{' '}
                <a href="" rel="noopener noreferrer">
                  Amazon OpenSearch Merlin
                </a>
              </div>
              <button
                className="rv-feedback-button"
                type="button"
                aria-label="Send feedback"
                onClick={() =>
                  window.open(
                    'https://amazon.enterprise.slack.com/archives/C0BMVEYUF0W',
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="url(#rvFeedbackGrad)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient
                      id="rvFeedbackGrad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="0%"
                    >
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#d946ef" />
                    </linearGradient>
                  </defs>
                  <path d="M4 4h16v12H8l-4 4V4z" />
                  <path d="M8 8h8" />
                  <path d="M8 12h5" />
                </svg>
                <span>Send feedback</span>
              </button>
            </div>
          </div>
        </div>
      </main>
      <SubagentsSidebar
        open={subagentsSidebarOpen}
        onClose={() => setSubagentsSidebarOpen(false)}
        tools={
          (messages.find((m) => m.id === subagentsSidebarMsgId)
            ?.tools as SidebarToolActivity[]) ?? []
        }
        selectedId={subagentsSidebarSelectedId}
      />
      <MemorySessionsPanel
        domainIdentifier={domainIdentifier}
        isOpen={memoryPanelOpen}
        onClose={() => setMemoryPanelOpen(false)}
        disabled={isInFlight}
        onImportSession={(newSessionId, msgs) => {
          setSessionId(newSessionId);
          setMessages(msgs as ChatMessage[]);
          setMemoryPanelOpen(false);
        }}
      />
    </div>
  );
}

// ─── Landing Sub-Components ──────────────────────────────────────────────────

function LandingHero({
  insightsLoading,
  hasActiveInsights,
  insightCount,
  onQuickCommand,
}: {
  insightsLoading: boolean;
  hasActiveInsights: boolean;
  insightCount: number;
  onQuickCommand: () => void;
}) {
  return (
    <div className="rv-landing__left">
      <div className="rv-hero-section">
        <div
          className="rv-hero-icon"
          style={{ position: 'relative', zIndex: 2 }}
        >
          <svg
            width="56"
            height="56"
            viewBox="0 0 24 24"
            fill="none"
            stroke="url(#heroGrad)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <defs>
              <linearGradient id="heroGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="var(--brand)" />
                <stop offset="100%" stopColor="var(--brand-2)" />
              </linearGradient>
            </defs>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            <circle
              cx="9"
              cy="10"
              r="0.8"
              fill="url(#heroGrad)"
              stroke="none"
            />
            <circle
              cx="12"
              cy="10"
              r="0.8"
              fill="url(#heroGrad)"
              stroke="none"
            />
            <circle
              cx="15"
              cy="10"
              r="0.8"
              fill="url(#heroGrad)"
              stroke="none"
            />
          </svg>
        </div>
        <h2 className="rv-hero-heading">Your AI Assistant</h2>
        <p className="rv-hero-sub">
          Ask questions, analyze data, and get intelligent insights directly
          from your infrastructure.
        </p>

        {!insightsLoading && !hasActiveInsights && (
          <div className="rv-no-insights-card">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
            <span>No active insights found for this domain</span>
          </div>
        )}

        <div className="rv-quick-cards">
          <button className="rv-quick-card" onClick={onQuickCommand}>
            <div className="rv-quick-card__header">
              <div className="rv-quick-card__icon">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 17l6-6-6-6" />
                  <path d="M12 19h8" />
                </svg>
              </div>
              <span className="rv-quick-card__title">Quick Commands</span>
            </div>
            <span className="rv-quick-card__desc">
              Type <strong>/</strong> to browse metrics, insights, and cluster
              data.
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Priority → color mapping */
const PRIORITY_COLORS: Record<string, string> = {
  HIGH: '#ef4444',
  MEDIUM: '#eab308',
  LOW: '#3b82f6',
};

/** Cache for per-insight AI summaries: key = displayName+priority */
const insightSummaryCache: Record<string, string> = {};

function InsightCard({
  insight,
  domainIdentifier,
  expanded,
  onToggle,
}: {
  insight: InsightSummary;
  domainIdentifier: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [summary, setSummary] = useState<string | null>(
    insightSummaryCache[`${insight.displayName}_${insight.priority}`] || null
  );
  const [loading, setLoading] = useState(false);
  const color = PRIORITY_COLORS[insight.priority] || '#6b7280';
  const cacheKey = `${insight.displayName}_${insight.priority}`;

  const handleExpand = useCallback(
    async (isExpanded: boolean) => {
      onToggle();
      if (isExpanded && !summary && !loading) {
        setLoading(true);
        try {
          const payload = JSON.stringify(insight, null, 2);
          const msg = `Summarize this insight concisely — what it means, its impact, and recommended actions:\n\n${payload}`;
          const response = await sendChatMessage(
            msg,
            null,
            domainIdentifier,
            true,
            true
          );
          const result = response.message || 'No summary available.';
          insightSummaryCache[cacheKey] = result;
          setSummary(result);
        } catch {
          setSummary('Failed to generate summary.');
        } finally {
          setLoading(false);
        }
      }
    },
    [summary, loading, insight, domainIdentifier, cacheKey, onToggle]
  );

  const headerContent = (
    <div className="rv-insight-card__header" style={{ borderLeftColor: color }}>
      <span className="rv-insight-card__name">{insight.displayName}</span>
      <span
        className="rv-insight-card__badge"
        style={{ background: `${color}20`, color }}
      >
        {insight.priority}
      </span>
    </div>
  );

  return (
    <div className="rv-insight-card" style={{ borderLeftColor: color }}>
      <ExpandableSection
        variant="footer"
        header={headerContent}
        expanded={expanded}
        onChange={({ detail }) => handleExpand(detail.expanded)}
      >
        {insight.description && (
          <p className="rv-insight-card__desc">{insight.description}</p>
        )}
        {loading && (
          <div className="rv-insight-card__loading">
            <Spinner size="normal" /> Generating AI summary…
          </div>
        )}
        {summary && !loading && (
          <div className="rv-insight-card__summary rv-markdown-content">
            <ResultRenderer blocks={safeParseResponse(summary)} />
          </div>
        )}
        {insight.detailError && (
          <div className="rv-insight-card__error">⚠️ {insight.detailError}</div>
        )}
      </ExpandableSection>
    </div>
  );
}

function InsightsPanel({
  insights,
  error,
  loading,
  onRetry,
  domainIdentifier,
}: {
  insights: InsightSummary[];
  error: string | null;
  loading: boolean;
  onRetry: () => void;
  domainIdentifier: string;
}) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Click outside → collapse all
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setExpandedIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="rv-landing__right" ref={panelRef}>
      <div className="rv-insights-panel">
        <div className="rv-insights-panel__header">
          <span className="rv-insights-panel__icon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
          </span>
          <span className="rv-insights-panel__title">
            Active Insights ({insights.length})
          </span>
          {error && (
            <span className="rv-insights-panel__badge rv-insights-badge--error">
              Error
            </span>
          )}
          {insights.length > 0 && !error && (
            <span className="rv-insights-panel__badge rv-insights-badge--ready">
              Ready
            </span>
          )}
        </div>
        <div className="rv-insights-panel__body">
          {error && (
            <div className="rv-insights-panel__error">
              <span>⚠️ {error}</span>
              <button className="rv-insights-retry" onClick={onRetry}>
                Retry
              </button>
            </div>
          )}
          {!loading && insights.length > 0 && (
            <div className="rv-insights-card-list">
              {insights.map((insight, i) => (
                <InsightCard
                  key={`${insight.displayName}-${i}`}
                  insight={insight}
                  domainIdentifier={domainIdentifier}
                  expanded={expandedIndex === i}
                  onToggle={() =>
                    setExpandedIndex(expandedIndex === i ? null : i)
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
